import { Navbar } from '@/components/layout/Navbar'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  BarChart3,
  Check,
  Globe2,
  TrendingUp,
  Smartphone,
  Star,
  Target,
  Lock,
  RefreshCw,
  Brain,
  ChevronRight,
  Play,
  Download
} from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '@/store/language-store'
import { Footerdemo } from '@/components/ui/footer-section'
import { useTheme } from '@/components/ThemeProvider'
import { motion } from 'framer-motion'

// Import hero image (add this when you provide the screenshots)
// @ts-ignore - Vite-specific import
// import heroImage from '../assets/flows-hero.png'

function StatsCounter({ value, duration = 2000 }: { value: string; duration?: number }) {
  const [currentValue, setCurrentValue] = useState('0')
  
  useEffect(() => {
    // Extract numeric value for animation
    const numericValue = value.match(/\d+/)
    if (!numericValue) {
      setCurrentValue(value)
      return
    }
    
    const targetNum = parseInt(numericValue[0])
    const suffix = value.replace(numericValue[0], '')
    const steps = 30
    const increment = targetNum / steps
    let current = 0
    
    const timer = setInterval(() => {
      current += increment
      if (current >= targetNum) {
        setCurrentValue(value)
        clearInterval(timer)
      } else {
        setCurrentValue(Math.floor(current) + suffix)
      }
    }, duration / steps)
    
    return () => clearInterval(timer)
  }, [value, duration])
  
  return <span>{currentValue}</span>
}

function TrustBadge({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-sm rounded-full text-sm font-medium text-text-primary dark:text-text-dark-primary border border-border dark:border-border-dark">
      {icon}
      {text}
    </div>
  )
}

export function Landing() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { language, setLanguage } = useLanguage()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  // Initialize and sync language on mount
  useEffect(() => {
    try {
      const persistedLanguage = localStorage.getItem('flows-language-storage')
      if (persistedLanguage) {
        const parsed = JSON.parse(persistedLanguage)
        if (parsed.state?.language && parsed.state.language !== language) {
          setLanguage(parsed.state.language)
          return
        }
      }

      const currentLanguage = i18n.language
      if (currentLanguage && currentLanguage !== language) {
        setLanguage(currentLanguage)
      }
    } catch (e) {
      console.error('Error initializing language:', e)
    }
  }, [language, setLanguage, i18n.language])

  return (
    <div className={cn(
      "min-h-screen bg-background-primary dark:bg-background-dark-primary",
      language === 'ar' ? 'font-arabic' : 'font-sans'
    )}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] overflow-hidden bg-gradient-to-br from-primary to-primary/90 dark:from-primary-dark dark:to-primary-dark/90">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M30 30m-2 0a2 2 0 1 1 4 0a2 2 0 1 1-4 0'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent dark:from-primary-dark/20"></div>
        </div>
        
        <div className="relative px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8 flex items-center min-h-[85vh]">
          <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
            {/* Left Column - Clean Content */}
            <div className="text-center lg:text-left">
              {/* Professional Trust Indicators */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex flex-wrap justify-center lg:justify-start gap-6 mb-8"
              >
                <div className="flex items-center gap-2 text-white/80 text-sm font-medium">
                  <div className="w-2 h-2 bg-positive rounded-full"></div>
                  50,000+ Active Users
                </div>
                <div className="flex items-center gap-2 text-white/80 text-sm font-medium">
                  <div className="w-2 h-2 bg-positive rounded-full"></div>
                  Bank-Level Security
                </div>
                <div className="flex items-center gap-2 text-white/80 text-sm font-medium">
                  <div className="w-2 h-2 bg-positive rounded-full"></div>
                  170+ Currencies
                </div>
              </motion.div>

              {/* Clean, Professional Title */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-6"
              >
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
                  Take Control of
                  <br />
                  <span className="text-positive">Your Finances</span>
                </h1>
                
                {/* Subtle feature highlight */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg text-white/90 text-sm font-medium mt-4">
                  <Brain className="w-4 h-4 text-yellow-400" />
                  AI-Powered Smart Budgeting
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0"
              >
                The complete financial management platform that helps you track expenses, 
                create intelligent budgets, and achieve your financial goals with confidence.
              </motion.p>

              {/* Professional CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10"
              >
                <Button
                  size="lg"
                  className="bg-positive hover:bg-positive/90 text-white text-lg px-8 py-4 rounded-xl shadow-lg transition-all duration-200 font-semibold"
                  onClick={() => navigate('/auth/signup')}
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-4 rounded-xl backdrop-blur-sm transition-all duration-200"
                >
                  <Play className="mr-2 h-5 w-5" />
                  View Demo
                </Button>
              </motion.div>

              {/* Clean Feature Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-white/80 text-sm"
              >
                <div className="flex items-center justify-center lg:justify-start gap-2">
                  <Check className="w-4 h-4 text-positive" />
                  <span>Free Forever Plan</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start gap-2">
                  <Check className="w-4 h-4 text-positive" />
                  <span>Multi-Platform Access</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start gap-2">
                  <Check className="w-4 h-4 text-positive" />
                  <span>Real-time Sync</span>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Professional Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative flex justify-center items-center"
            >
              {/* Main Dashboard Preview */}
              <div className="relative z-20 w-full max-w-lg">
                <div className="relative bg-background-secondary/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl rounded-2xl shadow-2xl p-6 border border-white/10">
                  {/* Dashboard Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-bold text-text-primary dark:text-text-dark-primary">Dashboard</h3>
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Financial Overview</p>
                    </div>
                    <div className="w-8 h-8 bg-positive/10 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 text-positive" />
                    </div>
                  </div>
                  
                  {/* Balance Card */}
                  <div className="bg-primary/5 dark:bg-primary-dark/5 rounded-xl p-4 mb-4 border border-primary/10 dark:border-primary-dark/10">
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-1">Total Balance</p>
                    <p className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">$24,847.50</p>
                    <div className="flex items-center gap-1 mt-2">
                      <TrendingUp className="w-4 h-4 text-positive" />
                      <span className="text-sm text-positive font-medium">+12.5% this month</span>
                    </div>
                  </div>
                  
                  {/* Quick Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-positive/5 rounded-lg p-3 border border-positive/10">
                      <p className="text-xs text-text-secondary dark:text-text-dark-secondary">Income</p>
                      <p className="text-lg font-semibold text-positive">$5,240</p>
                    </div>
                    <div className="bg-negative/5 rounded-lg p-3 border border-negative/10">
                      <p className="text-xs text-text-secondary dark:text-text-dark-secondary">Expenses</p>
                      <p className="text-lg font-semibold text-negative">$2,387</p>
                    </div>
                  </div>
                  
                  {/* Budget Progress */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-text-secondary dark:text-text-dark-secondary">Budget Progress</span>
                      <span className="text-sm font-medium text-positive">78% on track</span>
                    </div>
                    <div className="bg-border dark:bg-border-dark rounded-full h-2">
                      <div className="bg-gradient-to-r from-positive to-yellow-400 rounded-full h-2" style={{ width: '78%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subtle accent elements */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -right-4 z-10 bg-positive/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  Goal Achieved
                </div>
              </motion.div>
              
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-4 -left-4 z-10 bg-white/90 backdrop-blur-sm text-primary dark:text-primary-dark px-4 py-2 rounded-lg shadow-lg text-sm font-medium border border-white/20"
              >
                <div className="flex items-center gap-2">
                  <Brain className="w-3 h-3 text-yellow-500" />
                  Smart Insights
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof / Stats with Enhanced Design */}
      <section className="py-24 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border-b border-border dark:border-border-dark relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/5 to-primary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5"></div>
        
        <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-6">
              Trusted by Thousands Worldwide
            </h2>
            <p className="text-xl md:text-2xl text-text-secondary dark:text-text-dark-secondary max-w-3xl mx-auto">
              Join the community that's taking control of their finances
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "50K+", label: "Active Users", color: "text-secondary dark:text-primary-dark" },
              { value: "$2M+", label: "Money Saved", color: "text-positive" },
              { value: "170+", label: "Currencies", color: "text-secondary dark:text-primary-dark" },
              { value: "4.9/5", label: "User Rating", color: "text-secondary dark:text-primary-dark" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className={`text-5xl md:text-6xl font-bold ${stat.color} mb-3 transition-transform group-hover:scale-110`}>
                  <StatsCounter value={stat.value} />
                </div>
                <div className="text-text-secondary dark:text-text-dark-secondary font-semibold text-lg">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 bg-background-primary dark:bg-background-dark-primary">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-6">
              Everything You Need to Master Your Finances
            </h2>
            <p className="text-xl text-text-secondary dark:text-text-dark-secondary max-w-3xl mx-auto">
              From smart expense tracking to AI-powered budgeting, Flows.Cash provides 
              all the tools you need to take control of your financial future.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Brain className="w-8 h-8 text-secondary dark:text-primary-dark" />}
              title="AI-Powered Smart Budgeting"
              description="Our intelligent budget assistant learns your spending patterns and creates personalized budgets that actually work for your lifestyle."
              highlight="NEW"
            />
            <FeatureCard
              icon={<BarChart3 className="w-8 h-8 text-secondary dark:text-primary-dark" />}
              title="Advanced Analytics & Insights"
              description="Beautiful charts, detailed reports, and actionable insights help you understand your money patterns and make better decisions."
            />
            <FeatureCard
              icon={<Globe2 className="w-8 h-8 text-secondary dark:text-primary-dark" />}
              title="Multi-Currency Mastery"
              description="Handle 170+ currencies with real-time exchange rates. Perfect for international users, travelers, and global businesses."
            />
            <FeatureCard
              icon={<RefreshCw className="w-8 h-8 text-secondary dark:text-primary-dark" />}
              title="Cross-Platform Sync"
              description="Seamlessly access your data across web and mobile apps. Start on your phone, finish on your computer."
            />
            <FeatureCard
              icon={<Target className="w-8 h-8 text-secondary dark:text-primary-dark" />}
              title="Goal Achievement System"
              description="Set savings goals, track progress, and celebrate milestones. Our system keeps you motivated and on track."
            />
            <FeatureCard
              icon={<Lock className="w-8 h-8 text-secondary dark:text-primary-dark" />}
              title="Bank-Level Security"
              description="Your financial data is protected with enterprise-grade encryption. We never share your information with third parties."
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-background-secondary/50 dark:bg-background-dark-secondary/50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-6">
              Get Started in 3 Simple Steps
            </h2>
            <p className="text-xl text-text-secondary dark:text-text-dark-secondary">
              From setup to financial mastery in minutes, not hours
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary dark:bg-primary-dark text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">
                Quick Setup
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                Create your account and connect your financial accounts. 
                Our guided onboarding gets you up and running in under 5 minutes.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-secondary dark:bg-primary-dark text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">
                Smart Tracking
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                Start tracking transactions with intelligent categorization. 
                Our AI learns your patterns and automates the boring stuff.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-positive text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">
                Achieve Goals
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                Watch your financial health improve with actionable insights, 
                budget alerts, and progress toward your savings goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-background-primary dark:bg-background-dark-primary">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-6">
              What Our Users Are Saying
            </h2>
            <p className="text-xl text-text-secondary dark:text-text-dark-secondary">
              Real stories from real people who transformed their finances
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <TestimonialCard
              quote="Flows.Cash completely transformed how I manage money. I've saved more in 6 months than I did in the previous 2 years!"
              author="Sarah Johnson"
              role="Small Business Owner"
              rating={5}
            />
            <TestimonialCard
              quote="The multi-currency support is a game-changer for my international business. Finally, an app that gets it right."
              author="Michael Chen"
              role="Digital Nomad"
            />
            <TestimonialCard
              quote="I love how the smart budgeting actually learns my spending habits. It's like having a personal financial advisor."
              author="Emma Davis"
              role="Marketing Manager"
              rating={5}
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-background-secondary/50 dark:bg-background-dark-secondary/50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-6">
              Choose Your Financial Freedom Plan
            </h2>
            <p className="text-xl text-text-secondary dark:text-text-dark-secondary">
              Start free and upgrade when you're ready for more power
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <PricingCard
              title="Free"
              price="$0"
              period="forever"
              description="Perfect for getting started with personal finance tracking"
              features={[
                "Track unlimited transactions",
                "2 financial accounts",
                "Basic budgeting tools",
                "Web and mobile apps",
                "Email support"
              ]}
              buttonText="Start Free"
              buttonAction={() => navigate('/auth/signup')}
            />
            <PricingCard
              title="Pro"
              price="$5"
              period="per month"
              description="Everything you need for complete financial control"
              features={[
                "Everything in Free",
                "Unlimited accounts",
                "Advanced analytics & reports",
                "AI-powered budget assistant",
                "Goal tracking & planning",
                "Priority support",
                "Data export (CSV, Excel)"
              ]}
              buttonText="Start Pro Trial"
              buttonAction={() => navigate('/auth/signup')}
              popular={true}
            />
            <PricingCard
              title="Enterprise"
              price="Custom"
              period="pricing"
              description="Tailored solutions for teams and businesses"
              features={[
                "Everything in Pro",
                "Team collaboration",
                "Advanced integrations",
                "Custom reporting",
                "Dedicated support",
                "SLA guarantee",
                "API access"
              ]}
              buttonText="Contact Sales"
              buttonAction={() => navigate('/contact')}
            />
          </div>
        </div>
      </section>

      {/* Mobile Apps */}
      <section className="py-20 bg-background-primary dark:bg-background-dark-primary">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-6">
                Take Your Finances Anywhere
              </h2>
              <p className="text-xl text-text-secondary dark:text-text-dark-secondary mb-8">
                Our mobile apps bring the full power of Flows.Cash to your pocket. 
                Track expenses on the go, scan receipts, and get real-time insights 
                wherever you are.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <Check className="w-6 h-6 text-positive mr-3" />
                  <span className="text-text-secondary dark:text-text-dark-secondary">Offline mode with sync when connected</span>
                </div>
                <div className="flex items-center">
                  <Check className="w-6 h-6 text-positive mr-3" />
                  <span className="text-text-secondary dark:text-text-dark-secondary">Scan receipts with automatic categorization</span>
                </div>
                <div className="flex items-center">
                  <Check className="w-6 h-6 text-positive mr-3" />
                  <span className="text-text-secondary dark:text-text-dark-secondary">Real-time notifications and budget alerts</span>
                </div>
                <div className="flex items-center">
                  <Check className="w-6 h-6 text-positive mr-3" />
                  <span className="text-text-secondary dark:text-text-dark-secondary">Biometric security (Face ID, Touch ID)</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-primary text-white hover:bg-primary/90 rounded-xl dark:bg-primary-dark dark:hover:bg-primary-dark/90"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download for iOS
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Get on Android
                </Button>
              </div>
            </div>

            <div className="relative">
              {/* Placeholder for mobile app screenshots */}
              <div className="relative bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 rounded-3xl p-8 aspect-square flex items-center justify-center border border-border dark:border-border-dark">
                <div className="text-center">
                  <Smartphone className="w-24 h-24 text-secondary dark:text-primary-dark mx-auto mb-4" />
                  <p className="text-text-secondary dark:text-text-dark-secondary font-medium">
                    Mobile App Screenshots
                    <br />
                    Coming Soon
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-background-secondary/50 dark:bg-background-dark-secondary/50">
        <div className="px-4 mx-auto max-w-4xl sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-text-secondary dark:text-text-dark-secondary">
              Everything you need to know about Flows.Cash
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="border-border dark:border-border-dark">
              <AccordionTrigger className="text-left text-lg font-semibold text-text-primary dark:text-text-dark-primary hover:text-secondary dark:hover:text-primary-dark">
                Is Flows.Cash really free to use?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary dark:text-text-dark-secondary text-base">
                Yes! Our Free plan includes unlimited transaction tracking, 2 accounts, 
                basic budgeting, and access to both web and mobile apps. No hidden fees, 
                no time limits. Upgrade to Pro when you need advanced features.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="border-border dark:border-border-dark">
              <AccordionTrigger className="text-left text-lg font-semibold text-text-primary dark:text-text-dark-primary hover:text-secondary dark:hover:text-primary-dark">
                How secure is my financial data?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary dark:text-text-dark-secondary text-base">
                We use bank-level encryption and security measures. Your data is encrypted 
                both in transit and at rest. We never share your personal information with 
                third parties, and you can export or delete your data at any time.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-border dark:border-border-dark">
              <AccordionTrigger className="text-left text-lg font-semibold text-text-primary dark:text-text-dark-primary hover:text-secondary dark:hover:text-primary-dark">
                Which currencies and countries do you support?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary dark:text-text-dark-secondary text-base">
                We support 170+ currencies with real-time exchange rates, including major 
                cryptocurrencies and precious metals. The app is available in English, 
                Spanish, French, German, Turkish, and Arabic.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-border dark:border-border-dark">
              <AccordionTrigger className="text-left text-lg font-semibold text-text-primary dark:text-text-dark-primary hover:text-secondary dark:hover:text-primary-dark">
                Can I use Flows.Cash on multiple devices?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary dark:text-text-dark-secondary text-base">
                Absolutely! Your data syncs seamlessly across all your devices. Start a 
                transaction on your phone and finish it on your computer. Available on 
                web browsers, iOS, and Android.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border-border dark:border-border-dark">
              <AccordionTrigger className="text-left text-lg font-semibold text-text-primary dark:text-text-dark-primary hover:text-secondary dark:hover:text-primary-dark">
                What makes the smart budgeting different?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary dark:text-text-dark-secondary text-base">
                Our AI-powered budget assistant learns your spending patterns and creates 
                personalized budgets that actually work. It adapts to your lifestyle changes 
                and provides intelligent recommendations to help you save more.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark">
        <div className="px-4 mx-auto max-w-4xl text-center sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Master Your Money?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Join 50,000+ users who've already taken control of their finances. 
            Start your journey to financial freedom today.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 text-xl px-10 py-4 rounded-xl shadow-xl dark:text-primary-dark"
              onClick={() => navigate('/auth/signup')}
            >
              Start Free Today
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 text-xl px-10 py-4 rounded-xl backdrop-blur-sm"
            >
              <Play className="mr-2 h-6 w-6" />
              Watch Demo
            </Button>
          </div>

          <p className="text-white/60 text-sm">
            No credit card required • Free forever plan • Cancel anytime
          </p>
        </div>
      </section>

      <Footerdemo />
    </div>
  )
}

function FeatureCard({ 
  icon, 
  title, 
  description, 
  highlight 
}: { 
  icon: React.ReactNode
  title: string
  description: string
  highlight?: string
}) {
  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      transition={{ duration: 0.2 }}
      className="relative bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 rounded-2xl p-8 shadow-lg hover:shadow-xl border border-border dark:border-border-dark group"
    >
      {highlight && (
        <div className="absolute -top-3 left-6 bg-gradient-to-r from-secondary to-secondary dark:from-primary-dark dark:to-primary-dark text-white px-3 py-1 rounded-full text-sm font-semibold">
          {highlight}
        </div>
      )}
      
      <div className="mb-4 p-3 bg-secondary/10 dark:bg-primary-dark/10 rounded-xl w-fit group-hover:scale-110 transition-transform duration-200">
        {icon}
      </div>
      
      <h3 className="text-xl font-bold text-text-primary dark:text-text-dark-primary mb-3">
        {title}
      </h3>
      
      <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">
        {description}
      </p>
    </motion.div>
  )
}

function TestimonialCard({
  quote,
  author,
  role,
  rating = 5
}: {
  quote: string
  author: string
  role: string
  rating?: number
}) {
  return (
    <div className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 rounded-2xl p-8 shadow-lg border border-border dark:border-border-dark">
      <div className="flex mb-4">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} className="w-5 h-5 text-secondary dark:text-primary-dark fill-current" />
        ))}
      </div>
      
      <blockquote className="text-text-secondary dark:text-text-dark-secondary mb-6 text-lg leading-relaxed">
        "{quote}"
      </blockquote>
      
      <div>
        <div className="font-semibold text-text-primary dark:text-text-dark-primary">
          {author}
        </div>
        <div className="text-text-secondary dark:text-text-dark-secondary">
          {role}
        </div>
      </div>
    </div>
  )
}

function PricingCard({
  title,
  price,
  period,
  description,
  features,
  buttonText,
  buttonAction,
  popular = false
}: {
  title: string
  price: string
  period: string
  description: string
  features: string[]
  buttonText: string
  buttonAction: () => void
  popular?: boolean
}) {
  return (
    <div className={cn(
      "relative bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 rounded-3xl p-8 shadow-lg border",
      popular 
        ? "border-secondary dark:border-primary-dark scale-105 shadow-2xl" 
        : "border-border dark:border-border-dark"
    )}>
      {popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-secondary to-secondary dark:from-primary-dark dark:to-primary-dark text-white px-6 py-2 rounded-full text-sm font-semibold">
          Most Popular
        </div>
      )}
      
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
          {title}
        </h3>
        <div className="text-4xl font-bold text-text-primary dark:text-text-dark-primary mb-1">
          {price}
          <span className="text-lg font-normal text-text-secondary dark:text-text-dark-secondary">
            /{period}
          </span>
        </div>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          {description}
        </p>
      </div>
      
      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className="w-5 h-5 text-positive mr-3 mt-0.5 flex-shrink-0" />
            <span className="text-text-secondary dark:text-text-dark-secondary">{feature}</span>
          </li>
        ))}
      </ul>
      
      <Button
        onClick={buttonAction}
        className={cn(
          "w-full py-3 text-lg rounded-xl transition-all duration-200",
          popular
            ? "bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl dark:bg-primary-dark dark:hover:bg-primary-dark/90"
            : "bg-background-secondary dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary border border-border dark:border-border-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
        )}
      >
        {buttonText}
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  )
}

